import arrowUpIcon from "@assets/images/arrow-up-white.svg";
import folderIcon from "@assets/images/folder-open-new.svg";
import infoIcon from "@assets/images/info.svg";
import warningIcon from "@assets/images/warning-red.svg";
import { Box, Button, CircularProgress, Typography } from "@material-ui/core";
import DialogProcessing from "@saleor/components/DialogProcessing/DialogProcessing";
import MyTypography from "@saleor/components/MyTypography";
import {
  useExcelUploadFileMutation,
  useValidExcelFileMutation
} from "@saleor/graphql";
import useNotifier from "@saleor/hooks/useNotifier";
import { FormikProps } from "formik";
import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { FileError, useDropzone } from "react-dropzone";
import InlineSVG from "react-inlinesvg";
import { useIntl } from "react-intl";
import FileLine from "src/alternatedMaterial/components/UploadFile/FileLine";

import DialogErrorLineExcelUpload, {
  IRefDialog
} from "./DialogErrorLineExcelUpload";
import DialogSuccessExcelUpload from "./DialogSuccessExcelUpload";
import useStyle from "./styles";

interface IFile {
  size: number;
  type: string;
  path: string;
  name: string;
}

export interface IError {
  field: string;
  message: string;
}
export interface ExcelUploadFileProps {
  formik: FormikProps<any>;
}
const ExcelUploadFile: React.FC<ExcelUploadFileProps> = props => {
  const { formik } = props;

  const classes = useStyle();
  const intl = useIntl();
  const notify = useNotifier();

  const {
    getRootProps,
    getInputProps,
    acceptedFiles,
    fileRejections
  } = useDropzone({
    multiple: false,
    accept: ".xlsx",
    maxSize: 30000000
  });

  const [file, setFile] = useState<IFile>();
  const [openDialogProcessing, setOpenDialogProcessing] = useState(false);
  const [errorLines, setErrorLines] = useState<IError[] | null>();
  const dialogErrorRef = useRef<IRefDialog | null>(null);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [records, setRecords] = useState("");
  const [fileName, setFileName] = useState("");
  const [errorUploadMessage, setErrorUploadMessage] = useState("");

  const onRemove = () => {
    setFile(null);
    setErrorLines(null);
    setErrorUploadMessage(null);
  };

  const [uploadFile, uploadState] = useExcelUploadFileMutation({
    onCompleted: data => {
      setOpenDialogProcessing(false);
      if (data?.uploadExcel?.status) {
        formik.resetForm();
        setRecords(data?.uploadExcel?.rows);
        setFileName(file.name);
        setFile(null);
        setOpenSuccessDialog(true);
        setErrorLines(null);
      } else {
        const errors = data?.uploadExcel?.errors;
        setErrorLines(errors);
        dialogErrorRef.current.onOpen(errors);
      }
    },
    onError: errors => {
      notify({
        status: "error",
        apiMessage: errors.message
      });
      setOpenDialogProcessing(false);
    }
  });

  const [validateFileName, validateFileNameState] = useValidExcelFileMutation({
    onCompleted: data => {
      setOpenDialogProcessing(false);
      if (!data?.validExcelFile?.valid) {
        setErrorUploadMessage(data?.validExcelFile?.errors[0]?.message);
      }
    },
    onError: errors => {
      notify({
        status: "error",
        apiMessage: errors.message
      });
      setOpenDialogProcessing(false);
    }
  });

  useEffect(() => {
    if (file) {
      validateFileName({ variables: { fileName: file.name } });
    }
  }, [file, validateFileName]);

  const onFileReady = () => {
    let result: any;
    if (acceptedFiles.length > 0) {
      result = acceptedFiles[0];
    } else if (fileRejections.length > 0) {
      result = fileRejections[0].file;
    }
    setFile(result);
    setErrorLines(null);
    setErrorUploadMessage(null);
  };

  useEffect(() => {
    onFileReady();
  }, [acceptedFiles, fileRejections]);

  const errorMessage = useMemo<Pick<FileError, "code"> | string>(() => {
    if (!!fileRejections[0]?.errors && file) {
      return fileRejections[0]?.errors[0].code;
    }
    return "";
  }, [fileRejections, file]);

  const canUpload =
    !errorMessage &&
    !!file &&
    !!formik?.values?.orderType &&
    !!formik?.values?.saleOrganization &&
    !errorUploadMessage &&
    !openDialogProcessing;

  const onUpload = () => {
    if (canUpload) {
      setOpenDialogProcessing(true);
      uploadFile({
        variables: {
          file,
          orderType: formik?.values?.orderType,
          saleOrg: formik?.values?.saleOrganization,
          uploadType: formik?.values?.uploadType,
          distributionChannel: formik?.values?.distributionChannel,
          division: formik?.values?.division
        }
      });
    }
  };

  const onExplainError = () => {
    dialogErrorRef.current.onOpen(errorLines);
  };

  const body = (
    <Box display={"flex"} flexDirection="column" flex={1}>
      <section className={classes.uploadBox}>
        <Box
          {...getRootProps({ className: "dropzone" })}
          flexDirection="column"
          alignItems={"center"}
          height="93%"
          justifyContent="center"
          display={"flex"}
        >
          <input {...getInputProps()} data-testid="input-upload-file" />
          <InlineSVG src={folderIcon} />
          <Typography className={classes.dragTitle}>
            Drag & Drop to upload file or
          </Typography>
          <Typography component={"u"} className={`${classes.browse} `}>
            Click here to browse
          </Typography>
        </Box>
      </section>
      <FileLine path={file?.path} onRemove={onRemove} />
    </Box>
  );

  const BottomMessage = () => {
    const infoMessage1 = intl.formatMessage({
      defaultMessage: "รองรับไฟล์นามสกุล",
      description: "รองรับไฟล์นามสกุล"
    });

    const infoMessage3 = intl.formatMessage({
      defaultMessage: "เท่านั้น",
      description: "เท่านั้น"
    });
    const infoMessage = () => (
      <>
        {infoMessage1} <strong>.xlsx</strong> {infoMessage3}
      </>
    );

    const errorLineMessage = intl.formatMessage({
      defaultMessage: "อัปโหลดไฟล์ไม่สำเร็จ",
      description: "errorLineMessage"
    });

    const explainMessage = intl.formatMessage({
      defaultMessage: "ดูข้อมูลเพิ่มเติม",
      description: "explainMessage"
    });
    const uploadedMessage1 = intl.formatMessage({
      defaultMessage: "กรุณากด",
      description: "uploadedMessage1"
    });
    const uploadedMessage3 = intl.formatMessage({
      defaultMessage: "เพื่อยืนยัน",
      description: "uploadedMessage3"
    });

    const uploadedMessage = () => (
      <>
        {uploadedMessage1} <strong>Upload File</strong> {uploadedMessage3}
      </>
    );

    const getErrorMessage = () => {
      const invalidMessage1 = intl.formatMessage({
        defaultMessage: "รองรับไฟล์นามสกุล",
        description: "invalidMessage1"
      });
      const invalidMessage2 = intl.formatMessage({
        defaultMessage: "เท่านั้น, กรุณาลองใหม่",
        description: "invalidMessage2"
      });

      const invalidMessage = () => (
        <>
          {invalidMessage1} <strong>.xlsx</strong> {invalidMessage2}
        </>
      );
      if (errorMessage === "file-too-large") {
        return intl.formatMessage({
          defaultMessage: "ไฟล์มีขนาดเกิน 30 MB, กรุณาลองใหม่",
          description: "fileTooLarge"
        });
      }
      if (errorMessage === "file-invalid-type") {
        return invalidMessage();
      }
      return fileRejections[0]?.errors[0]?.message;
    };

    const isShowWarningIcon = errorMessage || errorLines || errorUploadMessage;
    let messages: string | ReactNode = "";
    if (errorMessage) {
      messages = getErrorMessage();
    } else if (errorUploadMessage) {
      messages = errorUploadMessage;
    } else if (!canUpload) {
      messages = infoMessage();
    }
    return (
      <Box
        data-testid="bottom-message"
        display={"flex"}
        justifyContent={"space-between"}
      >
        <Box alignItems="center" display="flex" justifyContent={"center"}>
          <InlineSVG
            src={isShowWarningIcon ? warningIcon : infoIcon}
            className={classes.infoIcon}
            style={{ marginTop: 0 }}
          />
          {errorLines && (
            <React.Fragment>
              <Typography className={classes.note}>
                {errorLineMessage} &nbsp;
              </Typography>
              <Typography
                onClick={onExplainError}
                component={"u"}
                style={{ fontWeight: 700 }}
                className={classes.notes}
              >
                {explainMessage}
              </Typography>
            </React.Fragment>
          )}
          {!errorLines && (
            <Typography
              className={classes.note}
              style={{
                color:
                  errorMessage || errorUploadMessage ? "#E41B17" : "#555555"
              }}
            >
              {messages}
              {canUpload ? uploadedMessage() : " "}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      className={classes.container}
      data-testid="excel-upload-file"
      display={"flex"}
      flexDirection={"column"}
    >
      <MyTypography variant="subtitle1" className={classes.uploadTitle}>
        <span className={classes.uploadTitleBold}>Upload :</span> Excel File
      </MyTypography>
      {body}
      <Box display={"flex"} justifyContent={"space-between"} marginTop={"0px"}>
        <BottomMessage />
        <Button
          data-testid="button-upload-file"
          className={classes.button}
          onClick={onUpload}
          style={{
            backgroundColor: canUpload ? "#009FB8" : "#E0E0E0",
            cursor: canUpload ? "pointer" : "not-allowed",
            height: "40px"
          }}
        >
          {uploadState.loading || validateFileNameState.loading ? (
            <CircularProgress size={16} />
          ) : (
            <InlineSVG src={arrowUpIcon} />
          )}

          <Typography className={classes.buttonTitle}>
            {intl.formatMessage({
              defaultMessage: "Upload File"
            })}
          </Typography>
        </Button>
      </Box>
      <DialogProcessing
        open={openDialogProcessing}
        title="ระบบกำลังอัปโหลดไฟล์"
        description="กรุณารอสักครู่ / โปรดอย่ากด Refresh หรือปิดหน้าจอ"
      />
      <DialogErrorLineExcelUpload ref={dialogErrorRef} />
      <DialogSuccessExcelUpload
        isOpen={openSuccessDialog}
        onClose={() => setOpenSuccessDialog(false)}
        messageBody={fileName + " อัพโหลดเสร็จสมบูรณ์"}
        subContent={records}
      />
    </Box>
  );
};

export default ExcelUploadFile;
